import { z } from "zod";
import {
  AnswerReviewOutcome,
  REVIEW_COMMENT_MAX,
  REVIEW_MARKS_MAX,
  ReviewMark,
  marksProblem,
} from "./answer-review.ts";
import { HOMEWORK_ANSWER_MAX } from "./homework-answer.ts";

/**
 * Пометки преподавателя в ответе ученика текстом (1.9
 * `docs/homework-scratch-design.md`, просьба владельца 26.09.2026): форма
 * проверки, контракты ручек и чистые функции, общие у сервера и экрана.
 *
 * **Единица проверки это абзац**, как предложение у перевода: непустая строка
 * ответа. Пометки абзаца это те же `ReviewMark`, что у перевода, со смещениями
 * в тексте АБЗАЦА (единицы строки JS, UTF-16), до `REVIEW_MARKS_MAX` на абзац.
 * Вердикта на абзац нет; итог один на весь ответ (`outcome`).
 *
 * ⚠️ **Модель в этом пути не участвует никогда.** Свободный ответ ученика
 * несёт ПДн (1.8 проекта, раздел 5.9 CLAUDE.md), поэтому у пометки здесь
 * всегда `source: "teacher"`, и сервер ставит это сам.
 *
 * Живёт в корне пакета: эталонов здесь нет ни одного поля, а черновик от
 * ученика прячет предикат ручки на сервере, а не барьер пакета.
 */

/** Абзацев в ответе не больше: ответ в 10 000 знаков, абзац хотя бы в знак и перевод строки. */
export const ANSWER_REVIEW_PARAGRAPHS_MAX = Math.ceil(HOMEWORK_ANSWER_MAX / 2);

/**
 * Пометок на весь ответ не больше. Без общего потолка предел давали бы только
 * «10 на абзац» и «5000 абзацев», то есть 50 000 пометок с исправлением и
 * пояснением по 500 знаков: сотни мегабайт в одном теле записи. Двести это
 * вдесятеро больше, чем преподаватель поставит в самом длинном сочинении, и
 * от этого числа считается потолок тела ручки записи
 * (`routes/homework-answer-review.ts`).
 */
export const ANSWER_REVIEW_MARKS_TOTAL_MAX = 200;

/* ─────────────────────────────── абзацы ─────────────────────────────── */

export interface AnswerParagraph {
  /** Текст абзаца как набран, без `trim`: смещения пометок стоят в нём. */
  text: string;
  /** Где абзац начинается в тексте ответа. */
  start: number;
}

/**
 * Ответ по абзацам: непустые строки (разбиение по переводу строки любого
 * вида: `\r\n`, `\n`, `\r`). Строка из одних пробелов абзацем не считается:
 * слов в ней нет, пометить нечего. Текст абзаца байт в байт как в ответе.
 *
 * ⚠️ Одна функция на клиент и сервер: сервер сверяет по ней присланные абзацы
 * с копией текста, экран по ней же рисует. Две версии разбиения это пометки,
 * которые сервер отвергнет, а экран не поймёт почему.
 */
export function answerParagraphs(text: string): AnswerParagraph[] {
  const out: AnswerParagraph[] = [];
  const re = /\r\n|\n|\r/g;
  let at = 0;
  const push = (end: number): void => {
    const line = text.slice(at, end);
    if (line.trim() !== "") out.push({ text: line, start: at });
  };
  for (const match of text.matchAll(re)) {
    push(match.index);
    at = match.index + match[0].length;
  }
  push(text.length);
  return out;
}

/* ─────────────────────────────── форма ─────────────────────────────── */

export const AnswerReviewParagraph = z.object({
  text: z.string().max(HOMEWORK_ANSWER_MAX),
  marks: z.array(ReviewMark).max(REVIEW_MARKS_MAX),
});
export type AnswerReviewParagraph = z.infer<typeof AnswerReviewParagraph>;

/**
 * Проверка ответа текстом целиком: абзацы с пометками, общий комментарий и
 * итог. `outcome` в черновике это выбор преподавателя до возврата (его
 * читает и общий возврат с экрана перевода); у возвращённой он же стоит
 * колонкой строки.
 */
export const AnswerReviewContent = z.object({
  paragraphs: z
    .array(AnswerReviewParagraph)
    .max(ANSWER_REVIEW_PARAGRAPHS_MAX)
    .refine(
      (paragraphs) => paragraphs.reduce((sum, p) => sum + p.marks.length, 0) <= ANSWER_REVIEW_MARKS_TOTAL_MAX,
      { message: `пометок на ответ больше ${ANSWER_REVIEW_MARKS_TOTAL_MAX}` },
    ),
  comment: z.string().max(REVIEW_COMMENT_MAX).nullable().default(null),
  outcome: AnswerReviewOutcome.nullable().default(null),
});
export type AnswerReviewContent = z.infer<typeof AnswerReviewContent>;

/** Пустая проверка по тексту ответа: абзацы без пометок, без итога. */
export function emptyAnswerReview(text: string): AnswerReviewContent {
  return {
    paragraphs: answerParagraphs(text).map((p) => ({ text: p.text, marks: [] })),
    comment: null,
    outcome: null,
  };
}

/** Сколько всего пометок в проверке. */
export function answerReviewMarkCount(content: AnswerReviewContent): number {
  return content.paragraphs.reduce((sum, p) => sum + p.marks.length, 0);
}

/**
 * Серверная сверка проверки с КОПИЕЙ проверяемого текста (снятой сервером,
 * а не присланной клиентом). `null`: всё в порядке, иначе первая беда словами
 * для лога.
 *
 * - абзацы ровно те, что даёт `answerParagraphs(text)`: то же число, тот же
 *   текст байт в байт, тот же порядок;
 * - пометки каждого абзаца по правилу перевода (`marksProblem`);
 * - пометка только от преподавателя: модели в этом пути нет.
 */
export function answerReviewProblem(content: AnswerReviewContent, text: string): string | null {
  const expected = answerParagraphs(text);
  if (content.paragraphs.length !== expected.length) {
    return `абзацев ${content.paragraphs.length}, а в ответе ${expected.length}`;
  }
  for (let i = 0; i < expected.length; i += 1) {
    const got = content.paragraphs[i] as AnswerReviewParagraph;
    const want = expected[i] as AnswerParagraph;
    if (got.text !== want.text) return `абзац ${i + 1} не совпал с текстом ответа`;
    if (got.marks.some((m) => m.source !== "teacher")) return `в абзаце ${i + 1} пометка не преподавателя`;
    const problem = marksProblem(got.marks, got.text);
    if (problem !== null) return `абзац ${i + 1}: ${problem}`;
  }
  return null;
}

/**
 * Перенос черновика на новую отправку ученика (1.9, правило перевода 2.8,
 * применённое к абзацам). Абзац нового текста получает пометки абзаца
 * источника с тем же текстом байт в байт, ПЕРВОГО неиспользованного:
 * смещения в том же тексте верны. Остальные пометки отпадают.
 *
 * Комментарий переезжает (это недописанная работа преподавателя). Итог
 * переезжает, только если текст не изменился вовсе: «Принято» было сказано
 * про другой текст.
 *
 * `source` это черновик, чьи абзацы и есть разбиение `prevText`; разбиение
 * берётся из него, а не заново из `prevText`, чтобы пометки шли вместе со
 * своим абзацем.
 */
export function carryAnswerReview(args: {
  source: AnswerReviewContent;
  prevText: string;
  nextText: string;
}): AnswerReviewContent {
  const used = new Set<number>();
  const paragraphs = answerParagraphs(args.nextText).map((p): AnswerReviewParagraph => {
    const at = args.source.paragraphs.findIndex((old, i) => !used.has(i) && old.text === p.text);
    if (at < 0) return { text: p.text, marks: [] };
    used.add(at);
    return { text: p.text, marks: (args.source.paragraphs[at] as AnswerReviewParagraph).marks };
  });
  return {
    paragraphs,
    comment: args.source.comment,
    outcome: args.prevText === args.nextText ? args.source.outcome : null,
  };
}

/* ─────────────────────────── состояние в списке ─────────────────────────── */

/** Три факта по работе, те же, что у перевода (`HomeworkReviewState`). */
export interface ReviewFlagSet {
  awaiting: boolean;
  fix: boolean;
  checked: boolean;
}

/**
 * Состояние ответа текстом в списке (1.9). Смотрит только на ПОСЛЕДНИЙ
 * ответ: возврат прошлой версии про новую ничего не говорит.
 *
 *   ждёт        у последнего ответа нет возврата, и текст не стёрт
 *   исправить   последний ответ возвращён с «Исправить»
 *   проверено   последний ответ возвращён с «Принято»
 *
 * Черновик ничего не меняет: пока не возвращено, ответ ждёт.
 *
 * ⚠️ Зеркало выражения `ANSWER_FLAG_COLUMNS` в `apps/api/src/repo/
 * homework-answer-review.ts`, и разъехаться им нельзя.
 */
export function answerReviewFlags(args: {
  answerAt: number | null;
  erased: boolean;
  /** Итог возврата ИМЕННО последнего ответа, `null` если его не возвращали. */
  returnedOutcome: AnswerReviewOutcome | null;
}): ReviewFlagSet {
  if (args.answerAt === null) return { awaiting: false, fix: false, checked: false };
  return {
    awaiting: args.returnedOutcome === null && !args.erased,
    fix: args.returnedOutcome === "fix",
    checked: args.returnedOutcome === "ok",
  };
}

/**
 * Слияние фактов перевода и ответа текстом (1.9): каждый факт по «или».
 * Какой из них станет признаком строки, решает `homeworkReviewState` по роли,
 * тем же правилом, что между двумя переводами одной работы.
 */
export function mergeReviewFlags(...sets: readonly ReviewFlagSet[]): ReviewFlagSet {
  return {
    awaiting: sets.some((s) => s.awaiting),
    fix: sets.some((s) => s.fix),
    checked: sets.some((s) => s.checked),
  };
}

/* ───────────────────────────── контракты ───────────────────────────── */

/** Черновик преподавателя. `updatedAt` это предусловие следующей записи. */
export const AnswerReviewDraft = z.object({
  /** Какой ответ проверяется: момент отправки. */
  answerAt: z.number().int(),
  content: AnswerReviewContent,
  updatedAt: z.number().int(),
  /**
   * Черновик перенесён с прошлой отправки ученика, и преподаватель ещё не
   * правил его после переноса: момент того ответа, на котором он начинал.
   * Экран по нему говорит «ученик прислал новый ответ» и после перезагрузки.
   * Запись черновика его снимает. `null`: не переносился или уже правлен.
   */
  carriedFromAnswerAt: z.number().int().nullable().default(null),
});
export type AnswerReviewDraft = z.infer<typeof AnswerReviewDraft>;

/**
 * Возвращённая проверка вместе с копией проверенного текста. Неизменяема.
 * Копия живёт вместе с домашкой, хотя `homework.answer_text` стирается через
 * 30 суток (решение владельца 26.09.2026, 1.9 проекта).
 */
export const ReturnedAnswerReview = z.object({
  answerAt: z.number().int(),
  text: z.string(),
  content: AnswerReviewContent,
  outcome: AnswerReviewOutcome,
  returnedAt: z.number().int(),
});
export type ReturnedAnswerReview = z.infer<typeof ReturnedAnswerReview>;

/**
 * Ответ `GET /v1/homework/:homeworkId/answer/review` и тело отказа
 * `review_stale` (поле `current`). Только преподавателю (нужда `manage`).
 *
 * - `answer`: последний ответ; `text: null` означает «стёрт по сроку»,
 *   проверять нечего. `null`: ответа нет;
 * - `draft`: черновик, всегда на последнем ответе;
 * - `last`: последний возврат («прошлая проверка» рядом).
 */
export const AnswerReviewState = z.object({
  answer: z.object({ answerAt: z.number().int(), text: z.string().nullable() }).nullable(),
  draft: AnswerReviewDraft.nullable(),
  last: ReturnedAnswerReview.nullable(),
});
export type AnswerReviewState = z.infer<typeof AnswerReviewState>;

/**
 * Тело `PUT /v1/homework/:homeworkId/answer/review`: черновик целиком.
 * `answerAt` это какой ответ преподаватель видел, `updatedAt` какую версию
 * черновика правит (`null`: черновика ещё нет). Не тот ответ или не та
 * версия: 409 `review_stale` с `current`.
 */
export const SaveAnswerReviewRequest = z.object({
  answerAt: z.number().int(),
  updatedAt: z.number().int().nullable(),
  content: AnswerReviewContent,
});
export type SaveAnswerReviewRequest = z.infer<typeof SaveAnswerReviewRequest>;

export const SaveAnswerReviewResponse = z.object({ draft: AnswerReviewDraft });
export type SaveAnswerReviewResponse = z.infer<typeof SaveAnswerReviewResponse>;

export const AnswerReviewStaleDetails = z.object({ current: AnswerReviewState });
export type AnswerReviewStaleDetails = z.infer<typeof AnswerReviewStaleDetails>;
